import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { CalendarDays, Zap } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Skeleton } from '@/components/ui/skeleton';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka } from '@/utils/number';
import { pcmpUrgentRequestsQueryOptions } from '../../api/pcmpRequests.api';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import { PCMP_REQUEST_EMPTY_MESSAGE } from '../../constants/pcmpRequest.constants';
import type { PcmpRequestFilterController } from '../../hooks/usePcmpRequestFilters';
import type { PcmpRequestMineItem } from '../../types/pcmpRequest.types';
import type { PcmpUrgentRequestDay } from '../../types/pcmpUrgent.types';
import { PcmpDivisionTotals } from '../PcmpRequested/PcmpDivisionTotals';
import { PcmpRequestCategoryFilter } from './PcmpRequestCategoryFilter';
import { getPcmpRequestMineColumns } from './PcmpRequestColumns';
import { PcmpRequestRowActions } from './PcmpRequestRowActions';

const columns = getPcmpRequestMineColumns({
  renderActions: (item) => <PcmpRequestRowActions item={item} />,
});

/** The date bar that opens a day: Today tag, "(continued)" and the whole day's figures. */
function DayHeader({ day }: { day: PcmpUrgentRequestDay }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-l-4 border-l-primary bg-primary/5 px-4 py-2.5">
      <CalendarDays className="size-4 text-primary" aria-hidden />
      <h2 className="text-base font-semibold">{format(parseISO(day.date), 'dd MMM yyyy, EEEE')}</h2>
      {day.isToday ? (
        <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold tracking-wide text-primary-foreground uppercase">
          Today
        </span>
      ) : null}
      {day.continued ? (
        <span className="text-sm text-muted-foreground italic">(continued)</span>
      ) : null}
      <span className="ml-auto text-sm text-muted-foreground tabular-nums">
        {formatNumber(day.count)} request{day.count === 1 ? '' : 's'} · {formatTaka(day.amount)}
      </span>
    </div>
  );
}

/**
 * Urgent Requests (legacy LDS-772): every Urgent Payment request I raised,
 * newest first, day by day. A day opens with its division and region summary,
 * which covers the whole day even when its requests run on to the next page;
 * that page marks it continued and skips the summary. One pager for the list.
 */
export function PcmpUrgentRequestsView({
  controller,
}: {
  controller: PcmpRequestFilterController;
}) {
  const router = useRouter();
  const { urgentRequestsParams: params } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpUrgentRequestsQueryOptions(params),
  );

  useKeepPageInRange(params.page, data?.requests.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpRequestMineItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load your urgent requests"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        fill
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-11 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  const items = data.requests.items;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          <span className="font-medium text-foreground tabular-nums">
            {formatNumber(data.requests.meta.total)}
          </span>{' '}
          urgent payment request{data.requests.meta.total === 1 ? '' : 's'} you raised ·{' '}
          <span className="font-medium text-foreground tabular-nums">
            {formatTaka(data.totalAmount)}
          </span>
        </p>
        <PcmpRequestCategoryFilter
          value={controller.requestCategory}
          onChange={controller.setRequestCategory}
          actionPlanKey="URGENT_PAYMENT"
        />
      </div>
      {items.length === 0 ? (
        <DataTable
          data={items}
          columns={columns}
          fillHeight
          className="lg:min-h-0 lg:flex-1"
          emptyIcon={Zap}
          emptyMessage={PCMP_REQUEST_EMPTY_MESSAGE.URGENT_REQUESTS}
        />
      ) : (
        <div
          className={cn(
            'flex flex-col gap-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto',
            isFetching && 'opacity-70',
          )}
        >
          {data.days.map((day) => (
            <section key={day.date} className="flex flex-col gap-3">
              <DayHeader day={day} />
              {day.continued ? null : (
                <PcmpDivisionTotals
                  divisionTotals={day.divisionTotals}
                  regionTotals={day.regionTotals}
                />
              )}
              <DataTable
                data={items.slice(day.firstIndex, day.firstIndex + day.rowCount)}
                columns={columns}
                stickyFirstColumn
                onRowClick={openProposal}
              />
            </section>
          ))}
        </div>
      )}
      <DataTablePagination
        page={data.requests.meta.page}
        totalPages={data.requests.meta.totalPages}
        total={data.requests.meta.total}
        limit={data.requests.meta.limit}
        onPageChange={controller.setPage}
        limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
      />
    </>
  );
}
